import { Injectable } from '@angular/core';
import { OrganizationHttpService } from '@core/http/organization-http.service';
import { UserConfigManagementService } from '@core/services/user-config-management.service';
import { SessionService } from '@core/session.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { BaseEntityEditComponent } from '@shared/components/base-entity-edit/base-entity-edit.component';
import { StorageEnum } from '@shared/enums/storage.enum';
import { BaseEntityEditInterface } from '@shared/interfaces/base/base-entity-edit.interface';
import {
  OrganizationFilter,
  OrganizationInterface,
} from '@shared/interfaces/organization.interface';
import {
  BehaviorSubject,
  Observable,
  Subject,
  filter,
  map,
  switchMap,
} from 'rxjs';

export interface OrgInfoUpdate {
  id: number;
  slug: string;
  name?: string;
  subdomain?: string;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CommunitySelectorStoredService {
  private readonly selectedOrg$ = new BehaviorSubject<OrganizationFilter>(null);
  private readonly keyword$ = new BehaviorSubject<string>('');
  private readonly popularOrgs$ = new BehaviorSubject([]);
  private readonly orgInfoUpdated$ = new Subject<OrgInfoUpdate>();
  search$ = new Subject<void>();
  innovationSpaceReset$ = new Subject<void>();
  activeOrgSuccessfulSave$ = new Subject<boolean>();

  currentEntityComponent: BaseEntityEditComponent<BaseEntityEditInterface>;

  constructor(
    private readonly sessionService: SessionService,
    private readonly orgHttpService: OrganizationHttpService,
    private readonly userConfig: UserConfigManagementService
  ) {}

  get selectedOrg() {
    return this.selectedOrg$.asObservable();
  }

  get orgInfoUpdated(): Observable<OrgInfoUpdate> {
    return this.orgInfoUpdated$.asObservable();
  }

  get keyword() {
    return this.keyword$.asObservable();
  }

  get keywordSnapshot(): string {
    return this.keyword$.getValue();
  }

  get popularOrgs(): Observable<OrganizationFilter[]> {
    return this.popularOrgs$.asObservable();
  }

  setSelectedOrg(org: OrganizationFilter): void {
    this.selectedOrg$.next(org);
  }

  async syncRenamedOrg(
    id: number,
    slug: string,
    name?: string,
    subdomain?: string
  ): Promise<void> {
    if (!id) return;

    const orgFilterList: OrganizationFilter[] =
      (await this.userConfig.get(StorageEnum.orgFilterToken)) ?? [];

    const target = orgFilterList.find(org => org.id === id);

    if (target) {
      target.slug = slug ?? '';
      if (name) target.name = name;
      if (subdomain !== undefined) target.subdomain = subdomain;

      await this.userConfig.set(StorageEnum.orgFilterToken, orgFilterList);
    }

    const current = this.selectedOrg$.value;

    if (current?.id === id) {
      this.selectedOrg$.next({
        ...current,
        slug: slug ?? '',
        ...(name ? { name } : {}),
        ...(subdomain !== undefined ? { subdomain } : {}),
      });
    }

    this.orgInfoUpdated$.next({ id, slug, name, subdomain });
  }

  setKeyword(keyword: string): void {
    this.keyword$.next(keyword);
  }

  search() {
    this.search$.next();
  }

  setPopularOrgs(orgs): void {
    this.popularOrgs$.next(orgs);
  }

  getPopularOrg(
    ignoreOrgIds: number[]
  ): Observable<(OrganizationInterface & OrganizationFilter)[]> {
    return this.sessionService.apiReady$.pipe(
      filter(Boolean),
      switchMap(() => {
        return this.orgHttpService
          .getPopularOrgs({
            IgnoreIds: ignoreOrgIds,
          })
          .pipe(
            untilDestroyed(this),
            map((orgs: (OrganizationInterface & OrganizationFilter)[]) =>
              orgs.map(org => ({ ...org, isPopular: true }))
            )
          );
      })
    );
  }
}
